import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { LoaderComponent } from '@examdojo/core/loader';
import { SmallLayoutComponent, WideLayoutComponent } from '@examdojo/layout';
import {
  provideLocalMcqSelection,
  provideSingleAttemptSelfGradingSelection,
  QUESTION_CONTEXT,
  QuestionAttemptEntityQuery,
} from '@examdojo/question';
import { FooterPanelComponent } from '@examdojo/ui/footer-panel';
import { isNotNullish } from '@examdojo/util/nullish';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter, finalize, switchMap, tap } from 'rxjs';
import { NgClass } from '@angular/common';
import { QuestionComponent } from '../../../question-v2';
import { ChallengeReviewDataService } from './challenge-review-data.service';
import { ChallengeReviewDialogService } from './challenge-review-dialog.service';
import { ChallengeReviewQuestionSelectorComponent } from './challenge-review-question-selector.component';
import { ChallengeReviewQuestionSelectorService } from './challenge-review-question-selector.service';

@Component({
  selector: 'dojo-challenge-review-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FooterPanelComponent,
    ButtonComponent,
    TranslocoPipe,
    ChallengeReviewQuestionSelectorComponent,
    QuestionComponent,
    SmallLayoutComponent,
    WideLayoutComponent,
    LoaderComponent,
    NgClass,
  ],
  templateUrl: './challenge-review-dialog.component.html',
  host: { class: 'flex flex-col h-full' },
  providers: [
    ChallengeReviewDataService,
    ChallengeReviewQuestionSelectorService,
    provideLocalMcqSelection(),
    provideSingleAttemptSelfGradingSelection(),
    {
      provide: QUESTION_CONTEXT,
      useValue: { showHints: true, optionsSelectable: false, revealSolutionOnWrongAnswer: true },
    },
  ],
})
export class ChallengeReviewDialogComponent {
  constructor(
    private readonly challengeReviewDialogService: ChallengeReviewDialogService,
    private readonly challengeReviewDataService: ChallengeReviewDataService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
  ) {
    this.loadReviewDataEffect().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly challengeActivityId = input.required<string>();
  readonly isLoading = signal(false);

  private readonly challengeActivityId$ = toObservable(this.challengeActivityId);

  readonly state = connectState({
    activeQuestionAttempt: this.questionAttemptQuery.active$,
    isQuestionMultipleChoice: this.questionAttemptQuery.isQuestionMultipleChoice$,
  });

  close() {
    this.challengeReviewDialogService.dismiss();
  }

  private loadReviewDataEffect() {
    return this.challengeActivityId$.pipe(
      filter(isNotNullish),
      tap(() => this.isLoading.set(true)),
      switchMap((activityId) =>
        this.challengeReviewDataService.fetchReviewData(activityId).pipe(
          tap(() => this.challengeReviewDataService.setFirstQuestionAsActive(activityId)),
          finalize(() => this.isLoading.set(false)),
        ),
      ),
    );
  }
}
